import { useMutation, useQueryClient } from 'react-query';
import { useToast } from 'app/hooks/useToast';
import { useTranslation } from 'react-i18next';
import { ApiClientError } from 'api/client';
import { queryKey } from 'constants/queryKey';
import { ProfileGroupsApi } from '../api/profileGroups';
import {
  ProfileGroupDetail,
  UpsertProfileGroupParams,
} from '../types/profileGroup';

interface UseUpsertProfileGroupMutationProps {
  onSuccess?: (group: ProfileGroupDetail) => void;
}

interface UpsertVariables {
  id?: number;
  data: UpsertProfileGroupParams;
}

export const useUpsertProfileGroupMutation = (
  props?: UseUpsertProfileGroupMutationProps,
) => {
  const toast = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, data }: UpsertVariables) =>
      id !== undefined
        ? ProfileGroupsApi.update(id, data)
        : ProfileGroupsApi.create(data),
    {
      onSuccess: (group, variables) => {
        queryClient.invalidateQueries(queryKey.profileGroupsList());
        if (variables.id !== undefined) {
          queryClient.invalidateQueries(
            queryKey.profileGroupDetail(variables.id),
          );
          toast(t('profileGroupsManagement.updateSuccess'), 'success');
        } else {
          toast(t('profileGroupsManagement.createSuccess'), 'success');
        }
        props?.onSuccess?.(group);
      },
      onError: (e: ApiClientError, variables) => {
        const fallbackKey =
          variables.id !== undefined
            ? 'profileGroupsManagement.updateError'
            : 'profileGroupsManagement.createError';
        toast(e?.userMessage ?? t(fallbackKey), 'error');
      },
    },
  );
};
